/** The Site step's AHJ: suggested from the design's address and location (`…/permit-ahjs/suggestions`), confirmed
 * with one click or picked from the others or a search of the AHJ database; shows what the plan set takes from it
 * (code editions, stamp rules, SolarAPP+, submission) with the date its facts were verified. US only. */
import { useQuery } from "@tanstack/react-query";
import { Check, ExternalLink, Search, X } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, unwrap, type S } from "../api/client";
import { Badge, Button, TextInput } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { codeLine, solarAppText, stampText } from "../lib/permitAhj";
import { useFormat } from "./format";
import type { Ws } from "./PermitWorkspace";

type Ahj = S["PermitAhj"];

/** A plain date ("2025-08-01") read at local noon, so the shown day does not move with the time zone. */
const dayAtNoon = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d);

export function AhjPicker({ ws }: { ws: Ws }) {
  const t = useT();
  const f = useFormat();
  const off = !ws.canEdit;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const suggestions = useQuery({
    queryKey: ["permit-ahj-suggestions", ws.designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/permit-ahjs/suggestions", { params: { path: { design_id: ws.designId } } })),
    staleTime: 60_000,
  });
  const search = useQuery({
    queryKey: ["permit-ahjs", query],
    enabled: open && query.trim().length >= 2,
    queryFn: () => unwrap(api.GET("/v1/permit-ahjs", { params: { query: { q: query.trim() } } })),
    staleTime: 60_000,
  });
  const items = suggestions.data?.items ?? [];
  const chosenId = ws.inputs.ahj_id;
  const merged = ws.preview.data?.electrical.ahj as (Record<string, unknown> & { codes?: Record<string, string> }) | null | undefined;
  const pick = (a: Ahj) => {
    ws.setInputs({ ahj_id: a.id, ahj: a.name });
    setOpen(false);
    setQuery("");
  };
  const clear = () => ws.setInputs({ ahj_id: undefined, ahj: undefined });
  const top = items[0];
  const confirmed = !!chosenId && merged?.confirmed === true;
  const list = query.trim().length >= 2 ? (search.data?.items ?? []).map((a) => ({ ahj: a, reason: a.level === "state" ? t("permit.ahjPicker.statewide") : a.county ? t("permit.ahjPicker.stateCounty", { state: a.state ?? "", county: a.county }) : a.state })) : items;

  return (
    <div className="pw-ahj" data-anchor="ahj">
      <div className="spread">
        <div>
          <div className="small muted">{t("permit.ahjPicker.heading")}</div>
          <b>{merged?.name ? String(merged.name) : ws.inputs.ahj || (top ? top.ahj.name : t("permit.ahjPicker.noneFound"))}</b>{" "}
          {merged && (confirmed ? <Badge tone="ok">{t("permit.ahjPicker.confirmed")}</Badge> : <Badge tone="warn">{t("permit.ahjPicker.suggested")}</Badge>)}
          {!merged && !chosenId && top && <Badge tone="warn">{t("permit.ahjPicker.suggested")}</Badge>}
          {merged?.reason && !confirmed ? <div className="small muted">{String(merged.reason)}</div> : null}
        </div>
        {!off && (
          <div className="row" style={{ gap: 6 }}>
            {!confirmed && top && (
              <Button size="sm" variant="primary" icon={<Check size={14} />} onClick={() => pick(top.ahj)}>
                {t("permit.ahjPicker.confirm", { level: top.ahj.level ?? "" })}
              </Button>
            )}
            <Button size="sm" icon={<Search size={14} />} onClick={() => setOpen((v) => !v)}>
              {open ? t("common.close") : t("permit.ahjPicker.pickAnother")}
            </Button>
            {chosenId && (
              <Button size="sm" variant="ghost" icon={<X size={14} />} onClick={clear} title={t("permit.ahjPicker.clearTitle")}>
                {t("permit.ahjPicker.clear")}
              </Button>
            )}
          </div>
        )}
      </div>
      {merged && (
        <ul className="pw-ahj-facts small">
          <li>{codeLine(merged.codes, t)}</li>
          <li>
            {stampText(t, "Structural", merged.stamp_structural as string)} · {stampText(t, "Electrical", merged.stamp_electrical as string)}
            {solarAppText(t, merged.solarapp as boolean | null) ? ` · ${solarAppText(t, merged.solarapp as boolean | null)}` : ""}
          </li>
          {(merged.submission as { method?: string; url?: string } | null)?.url && (
            <li>
              <FormattedMessage
                id={(merged.submission as { method?: string }).method ? "permit.ahjPicker.submission" : "permit.ahjPicker.submissionOnline"}
                values={{
                  method: (merged.submission as { method?: string }).method ?? "",
                  link: (c) => (
                    <a href={(merged.submission as { url: string }).url} target="_blank" rel="noreferrer">
                      {c}{" "}
                      <DirIcon>
                        <ExternalLink size={11} />
                      </DirIcon>
                    </a>
                  ),
                }}
              />
            </li>
          )}
          {merged.verified_on ? (
            <li className="muted">
              {t("permit.ahjPicker.verified", { date: f.date(dayAtNoon(String(merged.verified_on))), confidence: String(merged.confidence ?? "secondary") })}
            </li>
          ) : null}
        </ul>
      )}
      {open && !off && (
        <div className="pw-ahj-pick">
          <TextInput value={query} placeholder={t("permit.ahjPicker.searchPlaceholder")} onChange={(e) => setQuery(e.target.value)} autoFocus />
          <ul role="listbox" aria-label={t("permit.ahjPicker.listLabel")}>
            {list.map((s) => (
              <li key={s.ahj.id}>
                <button role="option" aria-selected={s.ahj.id === chosenId} onClick={() => pick(s.ahj)}>
                  <b>{s.ahj.name}</b>
                  <span className="small muted">{s.reason}</span>
                  <span className="small">{codeLine(s.ahj.codes, t)}</span>
                </button>
              </li>
            ))}
            {!list.length && <li className="small muted">{query.trim().length >= 2 ? t("permit.ahjPicker.noMatch") : t("permit.ahjPicker.noSuggestion")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
